import {
  List,
  Datagrid,
  SelectInput,
  BooleanInput,
  NumberInput,
  TextInput,
  useRecordContext,
  TopToolbar,
  CreateButton,
  ExportButton,
} from 'react-admin';
import { Chip, Box, Typography, Stack, useTheme, useMediaQuery } from '@mui/material';
import {
  LocalShipping as TruckIcon,
  DirectionsCar as CarIcon,
  Construction as ConstructionIcon,
  Agriculture as TractorIcon,
  Warning as WarningIcon,
  CheckCircle as ActiveIcon,
  PhotoCamera as PhotoIcon,
} from '@mui/icons-material';
import { PageTitle } from '../../components/PageTitle';

// Equipment type choices based on new schema
const equipmentTypes = [
  { id: 'CAR', name: 'Car' },
  { id: 'VAN', name: 'Van' },
  { id: 'TRUCK', name: 'Truck' },
  { id: 'TRACTOR', name: 'Tractor' },
  { id: 'TRAILER', name: 'Trailer' },
  { id: 'BUS', name: 'Bus' },
  { id: 'CONSTRUCTION', name: 'Construction Equipment' },
  { id: 'FORKLIFT', name: 'Forklift' },
  { id: 'CRANE', name: 'Crane' },
  { id: 'EXCAVATOR', name: 'Excavator' },
  { id: 'LOADER', name: 'Loader' },
  { id: 'BULLDOZER', name: 'Bulldozer' },
  { id: 'GRADER', name: 'Grader' },
  { id: 'COMPACTOR', name: 'Compactor' },
  { id: 'BACKHOE', name: 'Backhoe' },
  { id: 'SKID_STEER', name: 'Skid Steer' },
  { id: 'DUMP_TRUCK', name: 'Dump Truck' },
  { id: 'CONCRETE_MIXER', name: 'Concrete Mixer' },
  { id: 'PUMP_TRUCK', name: 'Pump Truck' },
  { id: 'TANKER', name: 'Tanker' },
  { id: 'REFRIGERATED_TRUCK', name: 'Refrigerated Truck' },
  { id: 'BOX_TRUCK', name: 'Box Truck' },
  { id: 'PICKUP_TRUCK', name: 'Pickup Truck' },
  { id: 'SEMI_TRUCK', name: 'Semi Truck' },
  { id: 'GARBAGE_TRUCK', name: 'Garbage Truck' },
  { id: 'TOW_TRUCK', name: 'Tow Truck' },
  { id: 'DELIVERY_VAN', name: 'Delivery Van' },
  { id: 'FLATBED', name: 'Flatbed' },
  { id: 'OTHER', name: 'Other' },
];

const ownershipTypes = [
  { id: 'OWNED', name: 'Owned' },
  { id: 'LEASED', name: 'Leased' },
];

// Custom field components
const VehicleImageField = () => {
  const record = useRecordContext();

  const getTypeInfo = (type: string) => {
    const vehicleTypes = ['CAR', 'VAN', 'TRUCK', 'BUS', 'PICKUP_TRUCK', 'DELIVERY_VAN'];
    const heavyEquipment = [
      'EXCAVATOR',
      'BULLDOZER',
      'CRANE',
      'LOADER',
      'GRADER',
      'COMPACTOR',
      'BACKHOE',
    ];
    const agricultural = ['TRACTOR'];

    if (vehicleTypes.includes(type)) {
      return { icon: <TruckIcon />, color: '#2196f3' };
    }
    if (heavyEquipment.includes(type)) {
      return { icon: <ConstructionIcon />, color: '#ff9800' };
    }
    if (agricultural.includes(type)) {
      return { icon: <TractorIcon />, color: '#4caf50' };
    }
    return { icon: <CarIcon />, color: '#9e9e9e' };
  };

  const typeInfo = getTypeInfo(record?.type || '');

  return (
    <Box
      sx={{
        width: 80,
        height: 56,
        overflow: 'hidden',
        bgcolor: record?.primaryPhotoFile?.url ? 'transparent' : typeInfo.color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid #e0e0e0',
        backgroundImage: record?.primaryPhotoFile?.url
          ? `url(${record.primaryPhotoFile.url})`
          : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {!record?.primaryPhotoFile?.url && (
        <Box
          sx={{ color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          {record?.type ? typeInfo.icon : <PhotoIcon />}
        </Box>
      )}
    </Box>
  );
};

const StatusField = () => {
  const record = useRecordContext();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {record?.active ? (
        <>
          <ActiveIcon sx={{ color: '#4caf50', fontSize: 20 }} />
          <Typography variant="body2" color="#4caf50">
            Active
          </Typography>
        </>
      ) : (
        <>
          <WarningIcon sx={{ color: '#ff9800', fontSize: 20 }} />
          <Typography variant="body2" color="#ff9800">
            Inactive
          </Typography>
        </>
      )}
    </Box>
  );
};

const TypeField = () => {
  const record = useRecordContext();
  if (!record?.type) {
    return null;
  }

  const getTypeInfo = (type: string) => {
    const vehicleTypes = ['CAR', 'VAN', 'TRUCK', 'BUS', 'PICKUP_TRUCK', 'DELIVERY_VAN'];
    const heavyEquipment = [
      'EXCAVATOR',
      'BULLDOZER',
      'CRANE',
      'LOADER',
      'GRADER',
      'COMPACTOR',
      'BACKHOE',
    ];
    const agricultural = ['TRACTOR'];

    if (vehicleTypes.includes(type)) {
      return { icon: <TruckIcon />, color: '#2196f3' };
    }
    if (heavyEquipment.includes(type)) {
      return { icon: <ConstructionIcon />, color: '#ff9800' };
    }
    if (agricultural.includes(type)) {
      return { icon: <TractorIcon />, color: '#4caf50' };
    }
    return { icon: <CarIcon />, color: '#9e9e9e' };
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const typeInfo = getTypeInfo(record.type);
  const displayName = equipmentTypes.find(t => t.id === record.type)?.name || record.type;

  return <Typography variant="body2">{displayName}</Typography>;
};

const VehicleDetailsField = () => {
  const record = useRecordContext();

  const details = [record?.manufacturedYear, record?.make, record?.model].filter(Boolean).join(' ');

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <VehicleImageField />
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {record?.name}
        </Typography>
        {details && (
          <Typography variant="caption" color="text.secondary">
            {details}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const IdentificationField = () => {
  const record = useRecordContext();

  return (
    <Stack spacing={0.5}>
      {record?.licensePlate && (
        <Typography variant="body2">
          <strong>Plate:</strong> {record.licensePlate}
        </Typography>
      )}
      {record?.vin && (
        <Typography variant="caption" color="text.secondary">
          VIN: {record.vin}
        </Typography>
      )}
      {!record?.licensePlate && !record?.vin && (
        <Typography variant="caption" color="text.secondary">
          No identification
        </Typography>
      )}
    </Stack>
  );
};

const OwnershipField = () => {
  const record = useRecordContext();
  if (!record?.ownershipType) {
    return <span>-</span>;
  }

  return (
    <Chip
      label={record.ownershipType}
      color={record.ownershipType === 'OWNED' ? 'primary' : 'secondary'}
      size="small"
      variant="outlined"
    />
  );
};

// Filter component
const EquipmentFilters = [
  <SelectInput key="type" source="type" choices={equipmentTypes} />,
  <BooleanInput key="active" source="active" label="Active Only" defaultValue={true} />,
  <SelectInput key="ownership" source="ownershipType" choices={ownershipTypes} />,
  <NumberInput key="year" source="manufacturedYear" />,
  <TextInput key="make" source="make" />,
];

// Custom toolbar
const ListActions = () => (
  <TopToolbar>
    <CreateButton label="Add Vehicle" />
    <ExportButton />
  </TopToolbar>
);

// Empty state component
const Empty = () => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    minHeight={400}
    sx={{ backgroundColor: '#f5f5f5', borderRadius: 2, p: 4 }}
  >
    <TruckIcon sx={{ fontSize: 80, color: '#e0e0e0', mb: 2 }} />
    <Typography variant="h5" gutterBottom>
      No Equipment Yet
    </Typography>
    <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
      Track your fleet equipment, vehicles, and assets. Manage maintenance schedules and equipment
      assignments.
    </Typography>
    <CreateButton label="Add Equipment" variant="contained" />
  </Box>
);

export const EquipmentList = () => {
  const theme = useTheme();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box>
      <PageTitle
        title="Vehicles"
        titleKey="pages.vehicles"
        subtitle="Manage your fleet and equipment"
        subtitleKey="pages.vehicles.subtitle"
        icon={<TruckIcon />}
      />
      <List
        filters={EquipmentFilters}
        actions={<ListActions />}
        empty={<Empty />}
        sort={{ field: 'name', order: 'ASC' }}
        perPage={25}
        sx={{ '& .RaList-main': { mt: 0 } }}
      >
      <Datagrid
        rowClick="show"
        bulkActionButtons={false}
        sx={{
          '& .RaDatagrid-row': {
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          },
        }}
      >
        <VehicleDetailsField label="Vehicle" />
        <TypeField label="Type" />
        <StatusField label="Status" />
        <IdentificationField label="Identification" />
        <OwnershipField label="Ownership" />
      </Datagrid>
    </List>
    </Box>
  );
};
